import React, { useEffect, useState } from "react";
import { Alert, Card, Col, Descriptions, Row, Select, Statistic, message, theme } from "antd";
import { useTranslation } from "react-i18next";
import MoneyInput from "../shared/MoneyInput";
import { formatCents } from "../shared/money";
import Amount from "../shared/Amount";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText } from "../api";
import * as api from "./api";

const NachkalkulationTab = () => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [vehicleDayRate, setVehicleDayRate] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .listProjects()
      .then(res => setProjects(res.data.projects || []))
      .catch(error => message.error(errorText(error)));
  }, []);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api
      .getNachkalkulation(projectId, { vehicleDayRate: vehicleDayRate || 0 })
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
  }, [projectId, vehicleDayRate]);

  const rows = data
    ? [
        {
          key: "plan",
          label: t("Plan price (offer / project, net)"),
          plan: data.plan.net,
          actual: data.revenue
        },
        { key: "material", label: t("Material"), actual: data.costs.material },
        { key: "subcontractors", label: t("Subcontractors"), actual: data.costs.subcontractors },
        {
          key: "personnel",
          label: `${t("Labour")} (${data.labour.hours} h)`,
          actual: data.costs.personnel
        },
        { key: "vehicle", label: t("Vehicle share"), actual: data.costs.vehicle },
        { key: "other", label: t("Other project costs"), actual: data.costs.other },
        { key: "total", label: t("Total costs"), actual: data.costs.total, strong: true },
        {
          key: "margin",
          label: t("Margin"),
          plan: data.planMargin,
          actual: data.margin,
          strong: true
        }
      ]
    : [];

  return (
    <div>
      <PageToolbar
        filters={
          <>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t("Select project")}
              value={projectId}
              onChange={setProjectId}
              options={projects.map(p => ({ value: p.id, label: `${p.name} (${p.status})` }))}
            />
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span>{t("Vehicle rate per employee-day")}</span>
              <MoneyInput value={vehicleDayRate} onChange={v => setVehicleDayRate(v || 0)} />
            </label>
          </>
        }
      />
      {data ? (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card
              size="small"
              title={
                <span className="acct-wrap" style={{ whiteSpace: "normal" }}>
                  {data.project.name}
                </span>
              }
              loading={loading}
            >
              <ResponsiveTable
                mobileLayout="table"
                className="acct-grid"
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={rows}
                columns={[
                  {
                    title: "",
                    dataIndex: "label",
                    render: (v, r) => <span style={{ fontWeight: r.strong ? 600 : 400 }}>{v}</span>
                  },
                  {
                    title: t("Plan"),
                    dataIndex: "plan",
                    align: "end",
                    render: v => (v === undefined || v === null ? "" : <Amount cents={v} />)
                  },
                  {
                    title: t("Actual"),
                    dataIndex: "actual",
                    align: "end",
                    render: (v, r) => (
                      <Amount
                        cents={v}
                        strong={r.strong}
                        style={{
                          color: r.key === "margin" && v < 0 ? token.colorError : undefined
                        }}
                      />
                    )
                  }
                ]}
              />
              <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                <Col xs={12} md={6}>
                  <Statistic
                    className="acct-kpi"
                    title={t("Margin %")}
                    value={data.marginPct === null ? "-" : `${data.marginPct} %`}
                    valueStyle={{ color: (data.margin || 0) < 0 ? token.colorError : undefined }}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    className="acct-kpi"
                    title={t("Invoiced (net)")}
                    value={formatCents(data.invoiced.net)}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    className="acct-kpi"
                    title={t("Still open")}
                    value={formatCents(data.invoiced.open)}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic className="acct-kpi" title={t("Hours")} value={data.labour.hours} />
                </Col>
              </Row>
              {!data.labour.available ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                  message={t(
                    "No attendance data with a project reference; labour costs are missing."
                  )}
                />
              ) : null}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card size="small" title={t("Pricing assumptions")}>
              {data.assumptions && data.expected ? (
                <Descriptions size="small" column={1} className="acct-wrap">
                  <Descriptions.Item label={t("Overhead")}>
                    {data.assumptions.overheadPct} % → <Amount cents={data.expected.overhead} />
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Risk")}>
                    {data.assumptions.riskPct} % → <Amount cents={data.expected.risk} />
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Full cost")}>
                    <Amount cents={data.expected.fullCost} />
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={`${t("Target price at")} ${data.assumptions.targetMarginPct} % ${t("margin")}`}
                  >
                    <Amount cents={data.expected.targetPrice} />
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Deviation revenue vs target")}>
                    <Amount
                      cents={data.expected.deviation}
                      style={{ color: data.expected.deviation < 0 ? token.colorError : undefined }}
                    />
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <span>{t("No pricing profile (Auftragssuche) configured.")}</span>
              )}
            </Card>
            <Card size="small" title={t("Labour by employee")} style={{ marginTop: 16 }}>
              <ResponsiveTable
                mobileLayout="table"
                className="acct-grid"
                size="small"
                pagination={false}
                rowKey="employee"
                dataSource={data.labour.byEmployee}
                columns={[
                  { title: t("Employee"), dataIndex: "name" },
                  { title: t("Hours"), dataIndex: "hours", align: "end" },
                  {
                    title: t("Rate/h"),
                    dataIndex: "rate",
                    align: "end",
                    render: v => <Amount cents={v} />
                  },
                  {
                    title: t("Cost"),
                    dataIndex: "cost",
                    align: "end",
                    render: v => <Amount cents={v} />
                  }
                ]}
              />
            </Card>
          </Col>
        </Row>
      ) : null}
    </div>
  );
};

export default NachkalkulationTab;
